<template>
  <div class="sun-cell-card">
    <div class="sun-cell-card__header">
      <div class="sun-cell-card__identity">
        <ControlIcon :name="SUN_OCCUPANCY_ICONS[occupancy]" size="1.125rem" class="sun-cell-card__icon" />
        <div>
          <div class="sun-cell-card__name">
            {{ sunLabel(SUN_OCCUPANCY_LABELS[occupancy]) }}
          </div>
          <div class="sun-cell-card__sub">
            {{ tt('Base position + events', 'Osnovni položaj + dogodki') }}
          </div>
        </div>
      </div>
      <button
        type="button"
        class="sun-cell-card__reset"
        :title="tt('Reset this cell to recommended settings', 'Ponastavi to celico na priporočene nastavitve')"
        @click="resetToDefaults">
        <ControlIcon name="refresh" size="0.875rem" />
      </button>
    </div>

    <div class="sun-cell-card__body">
      <div class="sun-cell-card__slider">
        <div class="sun-cell-card__slider-head">
          <span class="sun-cell-card__label">{{ tt('Base position', 'Osnovni položaj') }}</span>
          <span class="sun-cell-card__slider-value"> {{ cell.position }}% </span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          :value="cell.position"
          @input="update('position', Number(($event.target as HTMLInputElement).value))" />
        <div class="sun-cell-card__scale">
          <span>{{ tt('Open', 'Odprto') }}</span>
          <span>{{ tt('Closed', 'Zaprto') }}</span>
        </div>
      </div>

      <div class="sun-cell-card__slider">
        <div class="sun-cell-card__slider-head">
          <span class="sun-cell-card__label">{{ tt('Base tilt', 'Osnovni naklon') }}</span>
          <span class="sun-cell-card__slider-value">{{ cell.tilt }}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          :value="cell.tilt"
          @input="update('tilt', Number(($event.target as HTMLInputElement).value))" />
      </div>

      <!-- Sun tracking -->
      <label class="sun-cell-card__check">
        <input
          type="checkbox"
          :checked="cell.sun_tracking.enabled"
          @change="setTrackingEnabled(($event.target as HTMLInputElement).checked)" />
        <span>{{
          tt(
            'Continuous sun tracking (wins over events while sun is in window)',
            'Neprekinjeno sončno vodenje (prevlada nad dogodki, dokler je sonce v oknu)',
          )
        }}</span>
      </label>

      <template v-if="cell.sun_tracking.enabled">
        <div class="sun-cell-card__row">
          <label class="sun-cell-card__label">{{ tt('Track by', 'Vodenje po') }}</label>
          <div class="sun-cell-card__segmented">
            <button
              type="button"
              :class="{ 'sun-cell-card__segment--active': cell.sun_tracking.axis === 'azimuth' }"
              @click="updateTrack('axis', 'azimuth')">
              {{ tt('Azimuth', 'Azimut') }}
            </button>
            <button
              type="button"
              :class="{ 'sun-cell-card__segment--active': cell.sun_tracking.axis === 'altitude' }"
              @click="updateTrack('axis', 'altitude')">
              {{ tt('Altitude', 'Višina') }}
            </button>
          </div>
        </div>

        <div class="sun-cell-card__row">
          <label class="sun-cell-card__label">{{ tt('Slats by', 'Lamele po') }}</label>
          <div class="sun-cell-card__segmented">
            <button
              v-for="option in SLAT_OPTIONS"
              :key="option.value"
              type="button"
              :class="{ 'sun-cell-card__segment--active': cell.sun_tracking.slat_axis === option.value }"
              @click="updateTrack('slat_axis', option.value)">
              {{ sunLabel(option.label) }}
            </button>
          </div>
        </div>

        <div class="sun-cell-card__slider">
          <div class="sun-cell-card__slider-head">
            <span class="sun-cell-card__label">{{ tt('Position range', 'Razpon položaja') }}</span>
            <span class="sun-cell-card__slider-value">
              {{ cell.sun_tracking.min_position }}% – {{ cell.sun_tracking.max_position }}%
            </span>
          </div>
          <div class="sun-cell-card__slider-inputs">
            <input
              type="range"
              min="0"
              max="100"
              :value="cell.sun_tracking.min_position"
              @input="updateTrack('min_position', Number(($event.target as HTMLInputElement).value))" />
            <input
              type="range"
              min="0"
              max="100"
              :value="cell.sun_tracking.max_position"
              @input="updateTrack('max_position', Number(($event.target as HTMLInputElement).value))" />
          </div>
        </div>

        <div v-if="cell.sun_tracking.slat_axis !== 'off'" class="sun-cell-card__slider">
          <div class="sun-cell-card__slider-head">
            <span class="sun-cell-card__label">{{ tt('Slat tilt range', 'Razpon naklona lamel') }}</span>
            <span class="sun-cell-card__slider-value">
              {{ cell.sun_tracking.tilt_min }}% – {{ cell.sun_tracking.tilt_max }}%
            </span>
          </div>
          <div class="sun-cell-card__slider-inputs">
            <input
              type="range"
              min="0"
              max="100"
              :value="cell.sun_tracking.tilt_min"
              @input="updateTrack('tilt_min', Number(($event.target as HTMLInputElement).value))" />
            <input
              type="range"
              min="0"
              max="100"
              :value="cell.sun_tracking.tilt_max"
              @input="updateTrack('tilt_max', Number(($event.target as HTMLInputElement).value))" />
          </div>
        </div>

        <label class="sun-cell-card__check">
          <input
            type="checkbox"
            :checked="cell.sun_tracking.invert"
            @change="updateTrack('invert', ($event.target as HTMLInputElement).checked)" />
          <span>{{ tt('Invert tracking direction', 'Obrni smer vodenja') }}</span>
        </label>
      </template>

      <SunEventEditor :model-value="cell.events" @update:model-value="update('events', $event)" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  SUN_OCCUPANCY_ICONS,
  SUN_OCCUPANCY_LABELS,
  SunCellConfig,
  SunOccupancyKey,
  SunTrackParams,
  getSunCellDefaults,
  sunLabel,
} from '../../../utils/sunTracker';
import SunEventEditor from './SunEventEditor.vue';
import ControlIcon from '../../device-icons/ControlIcon.vue';
import { tt } from '../../../utils/consoleText';

const props = defineProps<{
  occupancy: SunOccupancyKey;
  modelValue: SunCellConfig;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: SunCellConfig): void;
}>();

const SLAT_OPTIONS = [
  { value: 'off', label: { en: 'Off', sl: 'Izklopljeno' } },
  { value: 'azimuth', label: { en: 'Azimuth', sl: 'Azimut' } },
  { value: 'altitude', label: { en: 'Altitude', sl: 'Višina' } },
] as const;

const cell = computed(() => props.modelValue);

function update<K extends keyof SunCellConfig>(key: K, value: SunCellConfig[K]) {
  emit('update:modelValue', { ...cell.value, [key]: value });
}

function updateTrack<K extends keyof SunTrackParams>(key: K, value: SunTrackParams[K]) {
  emit('update:modelValue', {
    ...cell.value,
    sun_tracking: { ...cell.value.sun_tracking, [key]: value },
  });
}

function setTrackingEnabled(enabled: boolean) {
  updateTrack('enabled', enabled);
}

function resetToDefaults() {
  emit('update:modelValue', {
    ...getSunCellDefaults(),
  });
}
</script>

<style scoped>
.sun-cell-card {
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
  overflow: hidden;
}

.sun-cell-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
}

.sun-cell-card__identity {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.sun-cell-card__icon {
  color: rgb(245, 158, 11);
}

.sun-cell-card__name {
  font-size: 13px;
  font-weight: 600;
  color: rgb(17, 24, 39);
}

.dark .sun-cell-card__name {
  color: rgb(255, 255, 255);
}

.sun-cell-card__sub {
  font-size: 11px;
  color: rgb(148, 163, 184);
}

.sun-cell-card__reset {
  flex-shrink: 0;
  color: rgb(148, 163, 184);
  border-radius: 999px;
  padding: 4px;
}

.sun-cell-card__reset:hover {
  color: rgb(245, 158, 11);
  background: rgba(245, 158, 11, 0.1);
}

.sun-cell-card__body {
  padding: 0 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sun-cell-card__slider-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sun-cell-card__label {
  font-size: 11px;
  color: rgb(148, 163, 184);
}

.sun-cell-card__slider-value {
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.sun-cell-card__slider input[type='range'] {
  width: 100%;
  accent-color: rgb(245, 158, 11);
}

.sun-cell-card__scale {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: rgb(148, 163, 184);
}

.sun-cell-card__slider-inputs {
  display: flex;
  gap: 8px;
}

.sun-cell-card__slider-inputs input[type='range'] {
  flex: 1;
}

.sun-cell-card__check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgb(107, 114, 128);
  cursor: pointer;
}

.dark .sun-cell-card__check {
  color: rgb(209, 213, 219);
}

.sun-cell-card__check input {
  accent-color: rgb(245, 158, 11);
}

.sun-cell-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.sun-cell-card__segmented {
  display: flex;
  gap: 4px;
  background: rgba(148, 163, 184, 0.12);
  border-radius: 999px;
  padding: 3px;
}

.sun-cell-card__segment--active {
  background: rgb(245, 158, 11);
  color: #fff;
  border-radius: 999px;
}

.sun-cell-card__segmented button {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  color: rgb(148, 163, 184);
}

.sun-cell-card__segmented button.sun-cell-card__segment--active {
  color: #fff;
}
</style>
